import {
    AlertTriangle,
    Download,
    FileSpreadsheet,
    RotateCcw,
} from "lucide-react";
import { useEffect, useId, useRef, type FC, type RefObject } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useOutputDocumentDownload } from "@/hooks/useOutputDocumentDownload";

export interface SpreadsheetViewerPanelProps {
    /** Lifecycle of the (non-PDF) document fetch, mirrored from the viewer. */
    status: "loading" | "ready" | "error";
    documentName: string;
    documentId: number;
    downloadAvailable: boolean;
    onRetry?: () => void;
    focusGuard?: RefObject<HTMLElement | null>;
    autoFocus?: boolean;
}

/** Human file-type label derived from the display filename — the panel is only
 * given the name, not the persisted `file_type`. Always carries text so the
 * badge is never color-only. */
function fileTypeLabel(name: string): string {
    const lower = name.toLowerCase();
    if (lower.endsWith(".xlsx")) return "XLSX";
    if (lower.endsWith(".csv")) return "CSV";
    return "Spreadsheet";
}

export const SpreadsheetViewerPanel: FC<SpreadsheetViewerPanelProps> = ({
    status,
    documentName,
    documentId,
    downloadAvailable,
    onRetry,
    focusGuard,
    autoFocus = true,
}) => {
    const displayName = documentName.trim() || "Untitled spreadsheet";
    const download = useOutputDocumentDownload();
    const downloadRef = useRef<HTMLButtonElement>(null);
    const unavailableMessageId = useId();

    useEffect(() => {
        if (!autoFocus || status !== "ready" || !downloadAvailable) return;
        if (focusGuard?.current?.contains(document.activeElement)) return;
        downloadRef.current?.focus();
    }, [autoFocus, downloadAvailable, status, focusGuard]);

    if (status === "loading") {
        return (
            <div
                data-testid="spreadsheet-viewer-loading"
                className="flex h-full flex-col items-center justify-center gap-4 p-8"
            >
                <Skeleton className="size-16 rounded-2xl" />
                <div className="flex w-full max-w-[15rem] flex-col items-center gap-2.5">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-3 w-56" />
                </div>
                <Skeleton className="h-9 w-32 rounded-lg" />
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
                <span className="bg-destructive/10 text-destructive flex size-14 items-center justify-center rounded-2xl">
                    <AlertTriangle size={26} strokeWidth={1.75} aria-hidden />
                </span>
                <div className="space-y-1">
                    <p className="text-foreground text-sm font-semibold">
                        Couldn&rsquo;t load this spreadsheet
                    </p>
                    <p className="text-muted-foreground mx-auto max-w-[16rem] text-sm">
                        Something went wrong fetching the file. Give it another
                        try.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onRetry}
                    className="gap-2"
                >
                    <RotateCcw size={15} aria-hidden />
                    Try again
                </Button>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col items-center justify-center gap-5 p-8 text-center">
            <span className="bg-primary/8 text-primary ring-primary/10 flex size-16 items-center justify-center rounded-2xl ring-1">
                <FileSpreadsheet size={30} strokeWidth={1.5} aria-hidden />
            </span>

            <div className="flex max-w-sm flex-col items-center gap-2.5">
                <div className="flex max-w-full items-center gap-2">
                    <h3
                        className="text-foreground truncate text-sm font-semibold"
                        title={displayName}
                    >
                        {displayName}
                    </h3>
                    {/* Text badge so the file type isn't conveyed by the icon
                        color alone (accessibility). */}
                    <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[0.625rem] font-medium tracking-wide uppercase">
                        {fileTypeLabel(displayName)}
                    </span>
                </div>
                <p className="text-muted-foreground text-sm">
                    Spreadsheets open in your spreadsheet app. Download the file
                    to view the full workbook.
                </p>
            </div>

            <div className="flex flex-col items-center gap-1.5">
                <Button
                    ref={downloadRef}
                    data-testid="spreadsheet-download"
                    variant="default"
                    size="sm"
                    aria-label={`Download ${displayName}`}
                    aria-busy={download.isPending}
                    aria-describedby={
                        downloadAvailable ? undefined : unavailableMessageId
                    }
                    disabled={!downloadAvailable || download.isPending}
                    onClick={() => download.mutate(documentId)}
                    className="gap-2"
                >
                    <Download size={15} aria-hidden />
                    Download
                </Button>
                {!downloadAvailable && (
                    <p
                        id={unavailableMessageId}
                        className="text-muted-foreground text-xs"
                    >
                        Download unavailable here
                    </p>
                )}
            </div>
        </div>
    );
};
